/**
 * useMcpTools - Hook for fetching MCP tools with filtering support
 * 
 * Fetches tools from an MCP service, optionally filtered by virtual server.
 * Includes search, category, and backend type filtering.
 * 
 * @example
 * ```tsx
 * const { tools, loading, error } = useMcpTools({
 *   serviceId: 'orchestration-service',
 *   virtualServerId: 'jira-automation',
 *   search: 'ticket'
 * });
 * ```
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import type { MCPTool, ToolsResponse, ToolFilterState } from '../types';

interface UseMcpToolsOptions {
  serviceId: string | null;
  virtualServerId?: string | null;
  search?: string;
  category?: string | null;
  backendType?: string | null;
}

interface UseMcpToolsResult {
  tools: MCPTool[];
  filteredTools: MCPTool[];
  loading: boolean;
  error: Error | null;
  refresh: () => void;
  categories: string[];
  providers: string[];
}

// In-memory cache with 3-minute TTL
const cache = new Map<string, { data: MCPTool[]; timestamp: number }>();
const CACHE_TTL_MS = 3 * 60 * 1000;

// Debounce helper
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

export function useMcpTools(options: UseMcpToolsOptions): UseMcpToolsResult {
  const { serviceId, virtualServerId, search = '', category, backendType } = options;
  
  const [tools, setTools] = useState<MCPTool[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Debounce search term
  const debouncedSearch = useDebounce(search, 300);

  const refresh = useCallback(() => {
    const cacheKey = `${serviceId}:${virtualServerId || 'all'}`;
    cache.delete(cacheKey);
    setRefreshKey(prev => prev + 1);
  }, [serviceId, virtualServerId]);

  // Fetch tools
  useEffect(() => {
    if (!serviceId) {
      setTools([]);
      setLoading(false);
      return;
    }

    let mounted = true;
    const cacheKey = `${serviceId}:${virtualServerId || 'all'}`;

    const fetchTools = async () => {
      // Check cache first
      const cached = cache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
        setTools(cached.data);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Build URL - if virtualServerId, fetch from virtual server endpoint
        const url = virtualServerId
          ? `/api/membership/mcp/virtual-servers/${encodeURIComponent(virtualServerId)}/tools`
          : `/api/membership/mcp/services/${encodeURIComponent(serviceId)}/tools`;

        const response = await fetch(url, { credentials: 'include' });

        if (!response.ok) {
          throw new Error(`Failed to fetch tools: ${response.statusText}`);
        }

        const data: ToolsResponse = await response.json();
        const toolsList = data.tools || [];

        // Update cache
        cache.set(cacheKey, { data: toolsList, timestamp: Date.now() });

        if (mounted) {
          setTools(toolsList);
          setLoading(false);
        }
      } catch (err) {
        console.error('[useMcpTools] Error:', err);
        if (mounted) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoading(false);
        }
      }
    };

    fetchTools();

    return () => {
      mounted = false;
    };
  }, [serviceId, virtualServerId, refreshKey]);

  // Extract unique categories and providers
  const { categories, providers } = useMemo(() => {
    const categorySet = new Set<string>();
    const providerSet = new Set<string>();

    tools.forEach(tool => {
      tool.categories?.forEach(cat => categorySet.add(cat));
      if (tool.metadata?.provider) {
        providerSet.add(tool.metadata.provider);
      }
    });

    return {
      categories: Array.from(categorySet).sort(),
      providers: Array.from(providerSet).sort(),
    };
  }, [tools]);

  // Apply filters
  const filteredTools = useMemo(() => {
    let results = tools;

    // Search filter
    if (debouncedSearch) {
      const term = debouncedSearch.toLowerCase();
      results = results.filter(tool =>
        tool.name.toLowerCase().includes(term) ||
        (tool.description || '').toLowerCase().includes(term) ||
        (tool.metadata?.provider || '').toLowerCase().includes(term)
      );
    }

    // Category filter
    if (category) {
      results = results.filter(tool => tool.categories?.includes(category));
    }

    // Backend type filter
    if (backendType) {
      results = results.filter(tool => tool.metadata?.backend_type === backendType);
    }

    return results;
  }, [tools, debouncedSearch, category, backendType]);

  return {
    tools,
    filteredTools,
    loading,
    error,
    refresh,
    categories,
    providers,
  };
}

/**
 * Clear the tools cache
 */
export function clearToolsCache(): void {
  cache.clear();
}

/**
 * Invalidate cache entries for a specific service
 */
export function invalidateToolsCache(serviceId: string): void {
  for (const key of cache.keys()) {
    if (key.startsWith(`${serviceId}:`)) {
      cache.delete(key);
    }
  }
}

